import React from "react";
import { Control } from "react-hook-form";
import {
	FormField,
	FormItem,
	FormControl,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@hipotecar/ui/input";
import { MoneyInput } from "@/components/ui/money-input";

interface LinkedNumericInputProps {
	label: string;
	required?: boolean;
	// Left input (form-controlled - SWAPPED)
	leftValue: number;
	leftSuffix?: string;
	leftMax?: number; // Maximum value for left input (e.g., 80 for percentage)
	onLeftChange: (value: number) => void;
	// Right input (uncontrolled - SWAPPED)
	rightFieldName: string;
	rightPrefix?: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	control: Control<any>;
	onRightChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	disabled?: boolean;
}

/**
 * Two linked numeric inputs with an equals sign between them.
 * Single label on top describes both inputs.
 * Example: "Cuota Inicial"  [S/ 16,000] = [10%]
 * Left input is form-controlled, right input is calculated
 */
const LinkedNumericInput: React.FC<LinkedNumericInputProps> = ({
	label,
	required = false,
	leftValue,
	leftSuffix,
	leftMax,
	onLeftChange,
	rightFieldName,
	rightPrefix,
	control,
	onRightChange,
	disabled = false,
}) => {
	return (
		<div>
			{/* Single label for both inputs */}
			<label className="label-text">
				{label}
				{required && <span className="text-red-500"> *</span>}
			</label>

			{/* Grid with left input, equals sign, right input */}
			<div className="grid grid-cols-[1fr_auto_1fr] gap-3">
				{/* Left Input (Form Controlled - Currency) */}
				<FormField
					control={control}
					name={rightFieldName}
					render={({ field }) => (
						<FormItem>
							<FormControl>
								<div className="relative">
									{rightPrefix && (
										<span className="absolute top-1/2 left-3 -translate-y-1/2 text-xs text-tertiary">
											{rightPrefix}
										</span>
									)}
									<MoneyInput
										value={field.value as number}
										onValueChange={(values) => {
											const value = values.floatValue || 0;
											field.onChange(value);
											if (onRightChange) {
												const syntheticEvent = {
													target: { value: value.toString() },
												} as React.ChangeEvent<HTMLInputElement>;
												onRightChange(syntheticEvent);
											}
										}}
										className={rightPrefix ? "pl-9" : ""}
										disabled={disabled}
									/>
								</div>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				{/* Equals Sign */}
				<div className="flex h-11 items-center justify-center text-tertiary">
					=
				</div>

				{/* Right Input (Uncontrolled - Percent) */}
				<div className="relative">
					<Input
						type="number"
						step="0.01"
						value={leftValue}
						onChange={(e) => {
							let val = parseFloat(e.target.value) || 0;
							// Enforce max if provided
							if (leftMax !== undefined && val > leftMax) {
								val = leftMax;
							}
							onLeftChange(val);
						}}
						max={leftMax}
						disabled={disabled}
						onFocus={(e) => e.target.select()}
						className={leftSuffix ? "pr-8" : ""}
					/>
					{leftSuffix && <span className="input-addon">{leftSuffix}</span>}
				</div>
			</div>
		</div>
	);
};

export default LinkedNumericInput;
